import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import {
  Grid,
  Button,
  Divider,
  TextField,
  CircularProgress,
  duration,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  Typography,
  Chip,
  Box,
  Card,
  CardContent,
} from "@material-ui/core";
import { Star, TrendingUp, Assessment } from "@material-ui/icons";

import toJs from "../../../../hoc/toJS";
import select from "../../../../utils/select";
import KPMonthYearPicker from "../../../../components/KPMonthYearPicker";
import JournalAreasCategoriesPanel from "../../../../components/JournalAreasCategoriesPanel";

import { notification } from "../../../../reducer/home/action";
import { getDOI } from "../../../../reducer/scientificResearch/action";
import {
  getSearchFilteredJournal,
  getJournalByIdOnScientificResearch,
  getSearchFilteredJournalAfterDOI,
} from "../../../../reducer/journal/action";
import { getAllProjectByProfileId } from "../../../../reducer/project/action";
import { Link, Tab } from "@material-ui/icons";
import { Autocomplete } from "@material-ui/lab";

function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}

function isValidDate(d) {
  return d instanceof Date && !isNaN(d);
}

function JournalInternational(props) {
  const { values, setValues, handleChangeForm } = props;

  const [searchText, setSearchText] = useState("");
  const debouncedSearchText = useDebounce(searchText, 300);

  // The Redux journalDetail slice is shared, so a previous paper's journal can still be
  // sitting there when we open a paper that has no journalId. Compute a "current paper's
  // journal detail" — null whenever the cached entry doesn't belong to this paper —
  // so the ranking chips and areas panel stay empty during the gap.
  const currentJournalDetail =
    props.journalDetail && props.journalDetail.id && props.journalDetail.id === values?.journalId
      ? props.journalDetail
      : null;

  // Load the current user's research projects once so the "Đề tài" picker has options.
  // Re-fetch is gated by the profile id to avoid redundant requests on every render.
  useEffect(() => {
    const pid = props.currentProfile?.id;
    if (pid) {
      props.getAllProjectByProfileId(pid);
    }
  }, [props.currentProfile?.id]);

  // Flatten the listById ({ researchProject } wrappers) into plain project objects so
  // the Autocomplete options have a stable shape.
  const projectOptions = React.useMemo(() => {
    const raw = props.projectListByProfile || [];
    return raw
      .map((row) => (row?.researchProject ? row.researchProject : row))
      .filter((p) => p && p.id);
  }, [props.projectListByProfile]);

  const selectedProject =
    projectOptions.find((p) => p.id === values?.linkedResearchProjectId) ||
    // Fall back to the preloaded LinkedResearchProject on the SR record (view / update
    // mode where the project may not be in the user's listById, e.g. admin viewing
    // someone else's paper).
    (values?.linkedResearchProject && values?.linkedResearchProject.id === values?.linkedResearchProjectId
      ? values.linkedResearchProject
      : null);
  // console.log("debouncedSearchText", debouncedSearchText, props.listSearch);

  useEffect(() => {
    if (values?.publication?.title) setSearchText(values?.publication?.title);
  }, [props.updating, values?.publication?.title]);

  // Seed the displayed input from the current paper's journalDetail (view / update
  // mode). When the paper has no journalId, or Redux still holds another paper's
  // journal, clear the input so the user doesn't see a stale title.
  useEffect(() => {
    const jd = props.journalDetail;
    if (jd && jd.id && jd.id === values?.journalId && jd.title) {
      const label = jd.year ? `${jd.title} (${jd.year})` : jd.title;
      setSearchText(label);
      return;
    }
    if (!values?.journalId) {
      setSearchText("");
    }
  }, [props.journalDetail?.id, props.journalDetail?.title, values?.journalId]);

  useEffect(() => {
    if (
      debouncedSearchText !== 0 &&
      debouncedSearchText !== props.journalSearchFilterText &&
      !props.isFetchingJournalSearchFilter
    )
      props.getSearchFilteredJournal(1, 20, { title: debouncedSearchText });
  }, [debouncedSearchText]);

  // console.log("props.DOI", props.DOI);

  useEffect(() => {
    if (!(Object.keys(props.DOI).length === 0 && props.DOI.constructor === Object)) {
      if (!props.DOI?.issued) {
        props.notification({ type: "warning", message: "Link DOI không hợp lệ, vui lòng kiểm tra lại" });
        return;
      }
      const datePart = props.DOI?.issued?.["date-parts"]?.[0];
      // console.log("doi", props.DOI, props.isNeedReload);
      // console.log(
      //   "datePart",
      //   datePart,
      //   props.DOI?.["container-title"].replace(/[&\/\\#,+()$~%.'":*?<>{}]/g, ""),
      //   props.DOI?.ISSN?.[0],
      //   (props.DOI?.ISSN?.[0] || props.DOI?.ISBN?.[0])?.replace("-", "")
      // );

      props.getSearchFilteredJournalAfterDOI(
        1,
        100,
        {
          title: props.DOI?.["container-title"].replace(/[&\/\\#,+()$~%.'":*?<>{}]/g, ""),
          issn: (props.DOI?.ISSN?.[0] || props.DOI?.ISBN?.[0])?.replace("-", ""),
          year: datePart.length >= 1 ? datePart[0] : null,
          similarity: 0.8,
        },
        props.DOI,
      );
    }
  }, [props.isNeedReload]);

  useEffect(() => {
    setValues({
      ...values,
      sjrBestQuartile: null,
      abdcRanking: null,
      topRank: null,
      coreCollection: null,
      impactFactor: null,
      impactFactorFiveYear: null,
      submissionToAcceptance: null,
    });
  }, [values?.journalId]);

  useEffect(() => {
    if (
      // props.listSearchAfterDOI && props.listSearchAfterDOI.length !== 0 &&
      props.infoFromDoiLink &&
      props.infoFromDoiLink.length !== 0
    ) {
      if (props.listSearchAfterDOI.length == 0) {
        props.notification({
          type: "warning",
          message: "Không tìm thấy tạp chí trong cơ sở dữ liệu. Hệ thống đã tự điền tên tạp chí vào phần tạp chí khác.",
          duration: 5000,
        });
      }
      let tempListAuthor = "";
      (props.infoFromDoiLink?.author || [])?.forEach((author) => {
        const tempAuthor = `${author.given} ${author.family}`;
        if (tempListAuthor === "") tempListAuthor += tempAuthor;
        else tempListAuthor += `, ${tempAuthor}`;
      });

      // Lây ngày nộp đơn từ issued
      const issuedDay = new Date(2021, 0, 1);
      const createdDay = new Date(2021, 0, 1);
      const datePartIssued = props.infoFromDoiLink.issued["date-parts"][0];
      const datePartCreated = props.infoFromDoiLink.created["date-parts"][0];

      if (datePartIssued.length >= 1) issuedDay.setFullYear(datePartIssued[0]);
      if (datePartCreated.length >= 1) createdDay.setFullYear(datePartCreated[0]);

      // console.log(props.infoFromDoiLink?.author);
      setValues({
        ...values,
        doi: `https://doi.org/${props.infoFromDoiLink.DOI}`,
        title: props.infoFromDoiLink.title,
        numberMainAuthor: props.infoFromDoiLink?.author?.filter((e) => e.sequence === "first")?.length,
        issn: props.infoFromDoiLink?.ISSN?.[0] || props.infoFromDoiLink?.ISBN?.[0],
        issue: props.infoFromDoiLink.issue === undefined ? "" : props.infoFromDoiLink.issue,
        listAuthor: tempListAuthor,
        page: props.infoFromDoiLink.page === undefined ? "" : props.infoFromDoiLink.page,
        publisher: props.infoFromDoiLink.publisher === undefined ? "" : props.infoFromDoiLink.publisher,
        volume: props.infoFromDoiLink.volume === undefined ? "" : props.infoFromDoiLink.volume,
        numOfMember: props.infoFromDoiLink.author?.length,
        journalId: props.listSearchAfterDOI[0]?.id ?? null,
        otherTitle: props.listSearchAfterDOI?.length === 0 ? props.infoFromDoiLink["container-title"] : "",
        submissionDay: datePartIssued[0] > new Date().getFullYear() ? new Date(createdDay) : new Date(issuedDay),
        acceptedDay: new Date(issuedDay),
        year: parseInt(props.infoFromDoiLink.issued["date-parts"]),
      });
    }
  }, [props.isFetchingAfterDOI]);

  // Tự động lấy thông tin xếp hạng khi có journalId từ DOI
  useEffect(() => {
    if (values?.journalId && values?.journalId !== 0) {
      props.getJournalByIdOnScientificResearch(values?.journalId);
    }
  }, [values?.journalId]);

  useEffect(() => {
    const { journalDetail } = props;
    if (values?.journalId !== 0 && journalDetail) {
      setValues({
        ...values,
        sjrBestQuartile: journalDetail?.sjrBestQuartile,
        abdcRanking: journalDetail?.abdcRanking,
        topRank: journalDetail?.topRank,
        coreCollection: journalDetail?.coreCollection,
        impactFactor: journalDetail?.impactFactor,
        impactFactorFiveYear: journalDetail?.impactFactorFiveYear,
        submissionToAcceptance: journalDetail?.submissionToAcceptance,
      });

      // Hiển thị thông báo khi xếp hạng được tự động tải
      if (journalDetail?.sjrBestQuartile || journalDetail?.abdcRanking || journalDetail?.impactFactor) {
        props.notification({
          type: "success",
          message: "✅ Đã tự động tải thông tin xếp hạng của tạp chí!",
        });
      }
    }
  }, [props.isFetchingJournalDetail]);

  const handleGetDataFromDOI = () => {
    const type1 = "https://doi.org/";
    const type2 = "doi.org/";
    if (values?.doi?.substr(0, type1.length) === type1) {
      props.getDOI(values?.doi);
    } else if (values?.doi?.substr(0, type2.length) === type2) {
      props.getDOI(`https://${values?.doi}`);
    } else {
      props.getDOI(`https://doi.org/${values?.doi}`);
    }
  };

  const handleGetRankFromJournal = () => {
    if (values?.journalId === 0) {
      props.notification({ type: "error", message: "Vui lòng nhập tạp chí" });
    } else {
      props.getJournalByIdOnScientificResearch(values?.journalId);
    }
  };

  const PhanLoaiTapChiKhac = [
    { value: "NONE", label: "Tạp chí quốc tế uy tín thuộc danh mục ISI hoặc Scopus" },
    {
      value: "KTPT",
      label: "Tạp chí KT & PT bản tiếng Anh, Công bố bài báo quốc tế trên tạp chí nước ngoài thuộc danh mục ACI",
    },
    { value: "OTHER", label: "Tạp chí quốc tế ngoài ISI hoặc Scopus" },
  ];

  //   "(1) Giáo dục đào tạo
  // (2) Kinh tế
  // (3) Quản trị kinh doanh
  // (4) Quản lý
  // (5) Khác"

  return (
    <>
      <Grid item xs={8}>
        <TextField
          fullWidth
          size="small"
          disabled={props.disabled}
          label="Link DOI"
          variant="outlined"
          value={values?.doi || ""}
          onChange={(event) => handleChangeForm("doi", event.target.value)}
          helperText={
            !values?.doi ? (
              <div style={{ color: "red" }}>Vui lòng upload file minh chứng nếu công bố không có link DOI</div>
            ) : null
          }
        />
      </Grid>
      <Grid item xs={1}>
        {/* Mở tab đến link doi nếu có link */}
        {/* <Button
          fullWidth
          variant="contained"
          color="default"
          onClick={() => {
            if (values?.doi) window.open(values?.doi, "_blank");
          }}
          disabled={props.disabled || !values?.doi}
        >
          Mở DOI
        </Button> */}
        {/* Thay bằng icon cho tôi */}
        <Button
          fullWidth
          variant="contained"
          disabled={props.disabled || !values?.doi}
          startIcon={<Link />}
          onClick={() => {
            if (values?.doi) window.open(values?.doi, "_blank");
          }}
          style={{ float: "right" }}
        >
          Mở
        </Button>
      </Grid>
      <Grid item xs={3}>
        <Button
          fullWidth
          variant="contained"
          color="primary"
          onClick={() => handleGetDataFromDOI()}
          disabled={
            props.disabled ||
            props.isFetchingDOI ||
            props.isFetchingAfterDOI ||
            values?.locked === "kpi" ||
            values?.locked === "reward" ||
            values?.lockedForTimeResearch
          }
        >
          {props.isFetchingDOI || props.isFetchingAfterDOI ? <CircularProgress size={24} /> : "Nhập từ link DOI"}
        </Button>
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          size="small"
          disabled={props.disabled}
          label="Tên bài báo"
          variant="outlined"
          value={values?.title || ""}
          onChange={(event) => handleChangeForm("title", event.target.value)}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          size="small"
          disabled={props.disabled}
          label="Nhà xuất bản"
          variant="outlined"
          value={values?.publisher || ""}
          onChange={(event) => handleChangeForm("publisher", event.target.value)}
        />
      </Grid>
      <Grid item xs={6}>
        <TextField
          fullWidth
          size="small"
          disabled={props.disabled}
          label="ISSN/ISBN"
          variant="outlined"
          value={values?.issn || ""}
          onChange={(event) => handleChangeForm("issn", event.target.value)}
        />
      </Grid>
      <Grid item xs={3}>
        <TextField
          fullWidth
          size="small"
          disabled={props.disabled}
          label="Số lượng tác giả"
          variant="outlined"
          type="numOfMember"
          value={values?.numOfMember || ""}
          onChange={(event) => handleChangeForm("numOfMember", parseInt(event.target.value), 10)}
          error={!(values?.numOfMember > 0)}
        />
      </Grid>
      <Grid item xs={3}>
        <TextField
          fullWidth
          size="small"
          disabled={props.disabled}
          label="Số lượng tác giả chính"
          variant="outlined"
          type="number"
          value={values?.numberMainAuthor || ""}
          onChange={(event) => handleChangeForm("numberMainAuthor", parseInt(event.target.value, 10))}
          error={!(values?.numberMainAuthor > 0)}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          size="small"
          disabled={props.disabled}
          label="Tác giả"
          variant="outlined"
          value={values?.listAuthor || ""}
          multiline
          rows={4}
          onChange={(event) => handleChangeForm("listAuthor", event.target.value)}
        />
      </Grid>
      <Grid item xs={2}>
        <TextField
          fullWidth
          size="small"
          disabled={props.disabled}
          label="Issue"
          variant="outlined"
          value={values?.issue || ""}
          onChange={(event) => handleChangeForm("issue", event.target.value)}
        />
      </Grid>
      <Grid item xs={2}>
        <TextField
          fullWidth
          size="small"
          disabled={props.disabled}
          label="Volume"
          variant="outlined"
          value={values?.volume || ""}
          onChange={(event) => handleChangeForm("volume", event.target.value)}
        />
      </Grid>
      <Grid item xs={2}>
        <TextField
          fullWidth
          size="small"
          disabled={props.disabled}
          label="Page"
          variant="outlined"
          value={values?.page || ""}
          onChange={(event) => handleChangeForm("page", event.target.value)}
        />
      </Grid>
      <Grid item xs={6}>
        <TextField
          fullWidth
          select
          size="small"
          disabled={props.disabled || values?.locked === "kpi" || values?.locked === "reward"}
          label="Mục đích"
          variant="outlined"
          value={values?.purposeId ?? "none"}
          SelectProps={{ native: true }}
          onChange={(event) => handleChangeForm("purposeId", parseInt(event.target.value, 10))}
        >
          {props.purposes.map((e) => (
            <option key={e.id} value={e.id}>
              {e.description}
            </option>
          ))}
        </TextField>
      </Grid>
      <Grid item xs={6}>
        <KPMonthYearPicker
          fullWidth
          disabled={props.disabled}
          label="Tháng/Năm công bố"
          required
          value={values?.submissionDay || "01/01/2025"}
          setValue={(newValue) => {
            // newValue có dạng "01/mm/yyyy"
            const parts = newValue.split("/");
            if (parts.length === 3) {
              const day = parseInt(parts[0], 10);
              const month = parseInt(parts[1], 10);
              const year = parseInt(parts[2], 10);

              // Lưu submissionMonth
              handleChangeForm("submissionMonth", month);

              // Lưu submissionDay là Date object với ngày 1 của tháng đó, giờ 12:00 để tránh lệch múi giờ
              const date = new Date(year, month - 1, day, 12, 0, 0);
              handleChangeForm("submissionDay", date);
            }
          }}
        />
      </Grid>
      <Grid item xs={6}>
        <KPMonthYearPicker
          fullWidth
          disabled={props.disabled}
          label="Tháng/Năm xuất bản"
          value={values?.acceptedDay || "01/01/2025"}
          setValue={(newValue) => {
            // newValue có dạng "01/mm/yyyy"
            const parts = newValue.split("/");
            if (parts.length === 3) {
              const day = parseInt(parts[0], 10);
              const month = parseInt(parts[1], 10);
              const year = parseInt(parts[2], 10);

              // Lưu acceptedMonth
              handleChangeForm("acceptedMonth", month);

              // Lưu acceptedDay là Date object với ngày 1 của tháng đó, giờ 12:00 để tránh lệch múi giờ
              const date = new Date(year, month - 1, day, 12, 0, 0);
              handleChangeForm("acceptedDay", date);
            }
          }}
        />
      </Grid>
      {/* <Grid item xs={4}>
        <TextField
          fullWidth
          disabled={props.disabled}
          size="small"
          type="number"
          label="Điểm GS-PGS"
          variant="outlined"
          value={values?.score?.toString() || ""}
          onChange={(event) => handleChangeForm("score", parseFloat(event.target.value))}
        />
      </Grid> */}
      {/* <Grid item xs={12}>
        <FormControl fullWidth variant="outlined" size="small">
          <InputLabel id="phanLoaiTapChiKhac-label" style={{ padding: "0 8px", backgroundColor: "white" }}>
            Phân loại tạp chí khác
          </InputLabel>
          <Select
            native
            fullWidth
            disabled={props.disabled}
            labelId="phanLoaiTapChiKhac-label"
            value={values?.phanLoaiTapChiKhac || "NONE"}
            onChange={(event) => handleChangeForm("phanLoaiTapChiKhac", event.target.value)}
          >
            {PhanLoaiTapChiKhac.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </FormControl>
      </Grid>{" "} */}
      {props.isFetchingJournal ? (
        <CircularProgress size={24} />
      ) : (
        <Grid item xs={12}>
          <Autocomplete
            freeSolo
            size="small"
            disabled={props.disabled}
            // disableClearable
            options={props.listSearch || []}
            getOptionLabel={(option) => (option ? `${option?.title} (${option?.year})` : "")}
            // onInputChange={(e) => e && setSearchText(e?.target?.value)}
            inputValue={searchText}
            onInputChange={(event, newValue) => setSearchText(newValue)}
            onChange={(event, val) => {
              if (val !== null) handleChangeForm("journalId", val.id);
              else handleChangeForm("journalId", 0);
            }}
            value={
              // Prefer the search-result match (insert mode user-typing); fall back to
              // currentJournalDetail (already guarded to match the current journalId) so
              // a stale Redux entry from a previously-opened paper can't sneak in.
              props.listSearch?.find((e) => e.id === values?.journalId) ||
              currentJournalDetail ||
              null
            }
            autoHighlight
            filterOptions={(x) => x}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Tên tạp chí"
                margin="normal"
                variant="outlined"
                InputProps={{ ...params.InputProps }}
              />
            )}
            style={{ marginBottom: -10, marginTop: -16 }}
          />
        </Grid>
      )}
      <Grid item xs={12}>
        <TextField
          fullWidth
          size="small"
          disabled={props.disabled}
          label="Tên tạp chí khác (trong trường hợp tạp chí không thuộc Scopus)"
          variant="outlined"
          value={values?.otherTitle || ""}
          onChange={(event) => handleChangeForm("otherTitle", event.target.value)}
        />
      </Grid>
      {/* Optional link to a research project (đề tài). Empty by default — paper does
          not need to belong to any project. */}
      <Grid item xs={12}>
        <Autocomplete
          size="small"
          disabled={props.disabled}
          options={projectOptions}
          getOptionLabel={(option) =>
            option ? `${option?.title || option?.code || "(không có tên)"}${option?.code ? ` — ${option.code}` : ""}` : ""
          }
          getOptionSelected={(option, val) => option?.id === val?.id}
          value={selectedProject}
          onChange={(event, val) => {
            // Only update the id. handleChangeForm is `setValues({...values, [name]: value})`,
            // so two back-to-back calls inside the same event each read the stale `values`
            // closure — the second clobbers the first. The displayed object is resolved
            // via selectedProject from projectOptions or the preloaded record on the SR.
            handleChangeForm("linkedResearchProjectId", val?.id || 0);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Đề tài (tùy chọn)"
              placeholder="Chọn đề tài nếu bài báo này thuộc 1 đề tài"
              margin="normal"
              variant="outlined"
            />
          )}
          style={{ marginBottom: -10, marginTop: -8 }}
        />
      </Grid>
      <Grid item xs={12} style={{ margin: "auto" }}>
        <Divider />{" "}
      </Grid>
      {/* Thông tin xếp hạng tự động */}
      <Grid item xs={12}>
        <Typography variant="h6" style={{ marginBottom: 16, color: "#1976d2", fontWeight: 600 }}>
          🏆 Thông tin xếp hạng tự động
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Card style={{ backgroundColor: "#f8f9fa", border: "2px solid #e3f2fd" }}>
          <CardContent>
            <Grid container spacing={2}>
              {/* Read from `currentJournalDetail` so a stale Redux entry (from a previously
                  opened paper) can't leak into a paper that has no linked journal yet. */}
              <Grid item xs={4}>
                <Box display="flex" alignItems="center" mb={1}>
                  <Star style={{ color: "#ff9800", marginRight: 8 }} />
                  <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                    Xếp loại Q
                  </Typography>
                </Box>
                <Chip
                  label={currentJournalDetail?.sjrBestQuartile || "Chưa có"}
                  style={{
                    backgroundColor: currentJournalDetail?.sjrBestQuartile ? "#4caf50" : "#e0e0e0",
                    color: currentJournalDetail?.sjrBestQuartile ? "white" : "#666",
                    fontWeight: 600,
                    fontSize: "14px",
                  }}
                />
              </Grid>

              {/* [VinUni] Ẩn ABDC Ranking
              <Grid item xs={4}>
                <Box display="flex" alignItems="center" mb={1}>
                  <Assessment style={{ color: "#2196f3", marginRight: 8 }} />
                  <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                    ABDC Ranking
                  </Typography>
                </Box>
                <Chip
                  label={currentJournalDetail?.abdcRanking || "Chưa có"}
                  style={{
                    backgroundColor: currentJournalDetail?.abdcRanking ? "#2196f3" : "#e0e0e0",
                    color: currentJournalDetail?.abdcRanking ? "white" : "#666",
                    fontWeight: 600,
                    fontSize: "14px",
                  }}
                />
              </Grid> */}

              <Grid item xs={4}>
                <Box display="flex" alignItems="center" mb={1}>
                  <Assessment style={{ color: "#ff5722", marginRight: 8 }} />
                  <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                    ISI/Scopus
                  </Typography>
                </Box>
                <Chip
                  label={currentJournalDetail?.coreCollection || "Chưa có"}
                  style={{
                    backgroundColor: currentJournalDetail?.coreCollection ? "#ff5722" : "#e0e0e0",
                    color: currentJournalDetail?.coreCollection ? "white" : "#666",
                    fontWeight: 600,
                    fontSize: "12px",
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <Box display="flex" alignItems="center" mb={1}>
                  <TrendingUp style={{ color: "#4caf50", marginRight: 8 }} />
                  <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                    Impact Factor
                  </Typography>
                </Box>
                <Box display="flex" gap={2}>
                  <Chip
                    label={`Current: ${currentJournalDetail?.jif || currentJournalDetail?.impactFactor || "N/A"}`}
                    style={{
                      backgroundColor:
                        currentJournalDetail?.jif || currentJournalDetail?.impactFactor ? "#4caf50" : "#e0e0e0",
                      color: currentJournalDetail?.jif || currentJournalDetail?.impactFactor ? "white" : "#666",
                      fontWeight: 600,
                    }}
                  />
                  <Chip
                    label={`5-Year: ${
                      currentJournalDetail?.jifFiveYear || currentJournalDetail?.impactFactorFiveYear || "N/A"
                    }`}
                    style={{
                      backgroundColor:
                        currentJournalDetail?.jifFiveYear || currentJournalDetail?.impactFactorFiveYear
                          ? "#4caf50"
                          : "#e0e0e0",
                      color:
                        currentJournalDetail?.jifFiveYear || currentJournalDetail?.impactFactorFiveYear
                          ? "white"
                          : "#666",
                      fontWeight: 600,
                    }}
                  />
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Divider style={{ margin: "8px 0 12px" }} />
                <JournalAreasCategoriesPanel
                  areasList={currentJournalDetail?.areasList || []}
                  categoriesList={currentJournalDetail?.categoriesList || []}
                  dense
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={8}>
        <Box
          style={{
            padding: "12px 16px",
            backgroundColor: "#e8f5e8",
            borderRadius: 8,
            border: "1px solid #4caf50",
            marginTop: 8,
          }}
        >
          <Typography variant="body2" style={{ color: "#2e7d32", fontWeight: 500 }}>
            ✅ Xếp hạng sẽ tự động được tải khi bạn nhập DOI và tìm thấy tạp chí trong hệ thống
          </Typography>
        </Box>
      </Grid>
      <Grid item xs={4}>
        <Button
          fullWidth
          variant="outlined"
          color="secondary"
          onClick={() => handleGetRankFromJournal()}
          disabled={
            props.disabled ||
            props.isFetchingJournalDetail ||
            values?.locked === "kpi" ||
            values?.locked === "reward" ||
            values?.lockedForTimeResearch
          }
          style={{
            marginTop: 8,
            border: "2px dashed #999",
            color: "#666",
          }}
        >
          {props.isFetchingJournalDetail && <CircularProgress size={24} />}
          {!props.isFetchingJournalDetail && "🔄 Tính lại xếp hạng"}
        </Button>
      </Grid>
      <Grid item xs={12} style={{ margin: "auto" }}>
        <Divider />{" "}
      </Grid>
    </>
  );
}

const mapStateToProps = (state) => ({
  DOI: select(state, "scientificResearchReducer", "DOI"),
  purposes: select(state, "scientificResearchReducer", "purposes"),
  listSearch: select(state, "journalReducer", "listSearch"),
  journalDetail: select(state, "journalReducer", "detailOnScientificResearch"),
  infoFromDoiLink: select(state, "journalReducer", "infoFromDoiLink"),
  listSearchAfterDOI: select(state, "journalReducer", "listSearchAfterDOI"),
  journalSearchFilterText: select(state, "journalReducer", "searchFilterText"),

  isNeedReload: select(state, "scientificResearchReducer", "isNeedReload"),
  isFetchingDOI: select(state, "scientificResearchReducer", "isFetchingDOI"),
  isFetchingJournal: select(state, "journalReducer", "isFetching"),
  isFetchingAfterDOI: select(state, "journalReducer", "isFetchingAfterDOI"),
  isFetchingJournalDetail: select(state, "journalReducer", "isFetchingDetailOnScientificResearch"),
  isFetchingJournalSearchFilter: select(state, "journalReducer", "isFetchingSearchFilter"),

  // Research project options for the "Đề tài" picker (paper → project link, optional).
  // listById is an array of { researchProject: {...}, ... } shaped responses scoped to
  // the current user's projects.
  projectListByProfile: select(state, "projectReducer", "listById"),
  currentProfile: select(state, "profileReducer", "detail"),
});

const mapDispatchToProps = (dispatch) => ({
  getDOI: (url) => dispatch(getDOI(url)),
  notification: (payload) => dispatch(notification(payload)),
  getJournalByIdOnScientificResearch: (id) => dispatch(getJournalByIdOnScientificResearch(id)),
  getSearchFilteredJournal: (page, pageSize, payload) => dispatch(getSearchFilteredJournal(page, pageSize, payload)),
  getSearchFilteredJournalAfterDOI: (page, pageSize, payload, doiInfo) =>
    dispatch(getSearchFilteredJournalAfterDOI(page, pageSize, payload, doiInfo)),
  getAllProjectByProfileId: (id) => dispatch(getAllProjectByProfileId(id)),
});

export default connect(mapStateToProps, mapDispatchToProps)(toJs(JournalInternational));

